<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'

const props = defineProps({
  record: Object,
  columns: Array,
  show: Boolean,
  tableName: String,
  isInline: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'edit'])

const searchQuery = ref('')
const compactMode = ref(false)
const activeFilters = ref([])
const newFilterInput = ref('')
const selectedFieldNames = ref([])
const fieldNameInput = ref('')

const filteredFields = computed(() => {
  let fields = props.columns || []
  
  // Apply field name filters (OR logic - show fields that match ANY selected field name OR live input)
  if (selectedFieldNames.value.length > 0 || fieldNameInput.value) {
    fields = fields.filter(column => {
      const fieldName = column.name.toLowerCase()
      
      // Check if field matches any selected field name chips
      const matchesChips = selectedFieldNames.value.length === 0 || 
        selectedFieldNames.value.some(chipName => 
          fieldName.includes(chipName.toLowerCase())
        )
      
      // Check if field matches live input
      const matchesLiveInput = !fieldNameInput.value || 
        fieldName.includes(fieldNameInput.value.toLowerCase())
      
      return matchesChips && matchesLiveInput
    })
  }
  
  // Apply value filters (AND logic - field must match ALL active filters)
  if (activeFilters.value.length > 0) {
    fields = fields.filter(column => {
      const fieldValue = props.record?.[column.name]
      const formattedValue = formatValue(fieldValue, column).toLowerCase()
      const fieldName = column.name.toLowerCase()
      
      // Check if field matches ALL active filters (AND logic)
      return activeFilters.value.every(filter => {
        const filterLower = filter.toLowerCase()
        return formattedValue.includes(filterLower) || 
               fieldName.includes(filterLower)
      })
    })
  }
  
  return fields
})

// Removed suggestions dropdown - using live filtering instead

const toggleCompactMode = () => {
  compactMode.value = !compactMode.value
}

const closeViewer = () => {
  emit('close')
}

const editRecord = () => {
  emit('edit')
}

const formatFieldType = (column) => {
  return column.type + (column.nullable ? '' : ' NOT NULL')
}

const formatValue = (value, column) => {
  if (value === null || value === undefined) {
    return 'NULL'
  }
  
  if (value === '') {
    return '(empty)'
  }
  
  const type = column.type.toLowerCase()
  if (type.includes('text') || type.includes('varchar')) {
    return String(value)
  }
  
  if (type.includes('date') || type.includes('time')) {
    return new Date(value).toLocaleString()
  }
  
  return String(value)
}

const getValueClass = (value) => {
  if (value === null || value === undefined) {
    return 'text-gray-400 italic'
  }
  if (value === '') {
    return 'text-gray-400 italic'
  }
  return 'text-gray-900'
}

// Multi-filter functions
const addFilter = () => {
  if (newFilterInput.value.trim() && !activeFilters.value.includes(newFilterInput.value.trim())) {
    activeFilters.value.push(newFilterInput.value.trim())
    newFilterInput.value = ''
  }
}

const removeFilter = (index) => {
  activeFilters.value.splice(index, 1)
}

const handleFilterKeydown = (event) => {
  if (event.key === 'Enter') {
    addFilter()
  }
}

const clearAllFilters = () => {
  activeFilters.value = []
}

// Field name chip functions
const addFieldName = (fieldName = null) => {
  const nameToAdd = fieldName || fieldNameInput.value.trim()
  if (nameToAdd && !selectedFieldNames.value.includes(nameToAdd)) {
    selectedFieldNames.value.push(nameToAdd)
    fieldNameInput.value = ''
  }
}

const removeFieldName = (index) => {
  selectedFieldNames.value.splice(index, 1)
}

const handleFieldNameKeydown = (event) => {
  if (event.key === 'Enter' && fieldNameInput.value.trim()) {
    event.preventDefault()
    addFieldName()
  }
}

// Removed selectSuggestion function - no longer needed

const clearAllFieldNames = () => {
  selectedFieldNames.value = []
}
</script>

<template>
  <div v-if="show" class="bg-white transition-all duration-300 flex flex-col h-full" :class="isInline ? '' : 'border-l border-gray-200 w-1/3 min-w-[480px]'">
    <!-- Header -->
    <div class="bg-gray-50 border-b border-gray-200" :class="isInline ? 'p-2' : 'p-4'">
      <div class="flex items-center justify-between" :class="isInline ? 'mb-2' : 'mb-3'">
        <div class="flex items-center">
          <h3 :class="isInline ? 'text-sm font-medium' : 'text-lg font-semibold'" class="text-gray-900">
            {{ isInline ? 'Record' : 'Record Details' }}
          </h3>
          <span class="ml-2 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
            {{ compactMode ? 'Compact' : 'Card' }}
          </span>
        </div>
        <div class="flex items-center space-x-2">
          <button 
            @click="toggleCompactMode"
            class="p-1"
            :class="compactMode ? 'text-blue-600 hover:text-blue-700' : 'text-gray-500 hover:text-gray-700'"
            :title="compactMode ? 'Switch to Card View' : 'Switch to Compact View'"
          >
            <i :class="compactMode ? 'fas fa-list' : 'fas fa-th-list'"></i>
          </button>
          <button 
            @click="editRecord"
            class="text-blue-600 hover:text-blue-700 p-1"
            title="Edit Record"
          >
            <i class="fas fa-edit"></i>
          </button>
          <button 
            @click="closeViewer"
            class="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      
      <!-- Search Fields -->
      <div class="space-y-3">
        <!-- Field Name Search with Chips -->
        <div class="space-y-2">
          <!-- Selected Field Name Chips -->
          <div v-if="selectedFieldNames.length > 0" class="flex flex-wrap gap-1">
            <div
              v-for="(fieldName, index) in selectedFieldNames"
              :key="index"
              class="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
            >
              <i class="fas fa-tag mr-1"></i>
              <span>{{ fieldName }}</span>
              <button
                @click="removeFieldName(index)"
                class="ml-1 text-green-600 hover:text-green-800 hover:bg-green-200 rounded-full w-4 h-4 flex items-center justify-center"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
            <button
              @click="clearAllFieldNames"
              class="inline-flex items-center px-2 py-1 text-red-600 hover:bg-red-100 rounded-full text-xs"
              title="Clear all field filters"
            >
              <i class="fas fa-trash text-xs"></i>
            </button>
          </div>

          <!-- Add Field Name Input with Live Filtering -->
          <div class="relative">
            <input
              v-model="fieldNameInput"
              @keydown="handleFieldNameKeydown"
              type="text"
              placeholder="Type to filter fields live or press Enter to add as chip..."
              :class="isInline ? 'py-1 text-xs' : 'py-2 text-sm'"
              class="w-full pl-8 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
            <i class="fas fa-filter absolute text-gray-400" :class="isInline ? 'left-2.5 top-1.5 text-xs' : 'left-2.5 top-2.5 text-sm'"></i>
            <button
              @click="addFieldName"
              class="absolute right-1 top-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
              :class="isInline ? 'text-xs' : 'text-sm'"
              title="Add current input as chip filter"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>

          <!-- Field Name Stats -->
          <div v-if="selectedFieldNames.length > 0" class="text-xs text-gray-500">
            <i class="fas fa-info-circle mr-1"></i>
            Showing fields matching: <strong>{{ selectedFieldNames.join(', ') }}</strong>
          </div>
        </div>

        <!-- Multi-Value Filter -->
        <div class="space-y-2">
          <!-- Add Filter Input -->
          <div class="relative">
            <input
              v-model="newFilterInput"
              @keydown="handleFilterKeydown"
              type="text"
              placeholder="Add value filter..."
              :class="isInline ? 'py-1 text-xs' : 'py-2 text-sm'"
              class="w-full pl-8 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
            <i class="fas fa-filter absolute text-gray-400" :class="isInline ? 'left-2.5 top-1.5 text-xs' : 'left-2.5 top-2.5 text-sm'"></i>
            <button
              @click="addFilter"
              class="absolute right-1 top-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
              :class="isInline ? 'text-xs' : 'text-sm'"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>

          <!-- Active Filters Display -->
          <div v-if="activeFilters.length > 0" class="flex flex-wrap gap-1">
            <div
              v-for="(filter, index) in activeFilters"
              :key="index"
              class="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
            >
              <span>{{ filter }}</span>
              <button
                @click="removeFilter(index)"
                class="ml-1 text-blue-600 hover:text-blue-800 hover:bg-blue-200 rounded-full w-4 h-4 flex items-center justify-center"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
            <button
              @click="clearAllFilters"
              class="inline-flex items-center px-2 py-1 text-red-600 hover:bg-red-100 rounded-full text-xs"
              title="Clear all filters"
            >
              <i class="fas fa-trash text-xs"></i>
            </button>
          </div>

          <!-- Filter Stats -->
          <div v-if="activeFilters.length > 0" class="text-xs text-gray-500">
            <i class="fas fa-info-circle mr-1"></i>
            Showing {{ filteredFields.length }} fields matching all {{ activeFilters.length }} filter{{ activeFilters.length > 1 ? 's' : '' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Fields List -->
    <div class="flex-1 overflow-y-auto">
      <!-- Compact Mode -->
      <div v-if="compactMode" :class="isInline ? 'p-2' : 'p-4'">
        <div class="space-y-1 font-mono" :class="isInline ? 'text-xs' : 'text-sm'">
          <div 
            v-for="column in filteredFields" 
            :key="column.name"
            class="flex items-center hover:bg-gray-50 px-2 py-1 rounded"
          >
            <!-- Value with fixed width -->
            <div :class="isInline ? 'w-32' : 'w-48'" class="flex-shrink-0 pr-4">
              <span 
                :class="getValueClass(record?.[column.name])"
                class="break-all"
              >
                {{ formatValue(record?.[column.name], column) }}
              </span>
            </div>
            
            <!-- Field name and metadata -->
            <div class="flex-1 flex items-center">
              <span class="font-medium text-gray-900">{{ column.name }}</span>
              <span v-if="column.key === 'PRI'" class="ml-2 px-1 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                PK
              </span>
              <span v-if="!column.nullable" class="ml-1 px-1 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                Required
              </span>
              <span v-if="column.autoIncrement" class="ml-1 px-1 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                Auto
              </span>
              <span class="ml-auto text-xs text-gray-500">{{ formatFieldType(column) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Card Mode -->
      <div v-else :class="isInline ? 'p-2 space-y-2' : 'p-4 space-y-3'">
        <div 
          v-for="column in filteredFields" 
          :key="column.name"
          class="bg-gray-50 rounded-lg p-3"
        >
          <!-- Field Header -->
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center">
              <span class="font-medium text-gray-900 text-sm">{{ column.name }}</span>
              <span v-if="column.key === 'PRI'" class="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                PK
              </span>
              <span v-if="!column.nullable" class="ml-1 px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                Required
              </span>
              <span v-if="column.autoIncrement" class="ml-1 px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                Auto
              </span>
            </div>
            <span class="text-xs text-gray-500">{{ formatFieldType(column) }}</span>
          </div>

          <!-- Field Value -->
          <div class="bg-white border border-gray-200 rounded p-2 min-h-[2rem] flex items-center">
            <span 
              :class="getValueClass(record?.[column.name])"
              class="text-sm break-all"
            >
              {{ formatValue(record?.[column.name], column) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div v-if="!isInline" class="bg-gray-50 border-t border-gray-200 p-4">
      <div class="flex space-x-3">
        <button
          @click="closeViewer"
          class="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
        >
          Close
        </button>
        <button
          @click="editRecord"
          class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
        >
          <i class="fas fa-edit mr-2"></i>
          Edit
        </button>
      </div>
    </div>
  </div>
</template> 